// File: src/pages/PageMenu/PurchasesMenu.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "../../axios";
import {
  Container,
  Grid,
  Typography,
  ButtonBase,
  Box,
  CircularProgress,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from "@mui/material";

// أيقونات Material-UI المستوردة
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ReplayIcon from "@mui/icons-material/Replay";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import GroupIcon from "@mui/icons-material/Group";
import BarChartIcon from "@mui/icons-material/BarChart";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PaymentIcon from "@mui/icons-material/Payment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";

import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

export default function PurchasesMenu() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const storedCompanyId = localStorage.getItem("companyId");
  const companyId = storedCompanyId ? Number(storedCompanyId) : null;

  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setError(t('purchasesMenu.companyIdNotFound'));
      return;
    }
    setLoading(true);
    axios
      .get("/InvoiceTypes", { params: { companyId } })
      .then((res) => setInvoiceTypes(res.data || []))
      .catch((err) => {
        console.error("❌ فشل في تحميل أنواع الفواتير:", err);
        setError(t('purchasesMenu.failedToLoadInvoiceTypes'));
      })
      .finally(() => setLoading(false));
  }, [companyId, t]);

  // دالة مساعدة لتحديد الأيقونة الصحيحة لأنواع الفواتير الديناميكية
  const getPurchaseTypeIcon = (name) => {
    const iconSize = { xs: 32, sm: 36, md: 40 };
    const iconColor = "#f59e0b";
    
    switch (name.trim()) {
      case "شراء":
      case "فاتورة شراء":
        return <ReceiptLongIcon sx={{ fontSize: iconSize, color: iconColor }} />;
      case "مرتجع مشتريات":
      case "فاتورة مرتجع مشتريات":
        return <ReplayIcon sx={{ fontSize: iconSize, color: iconColor }} />;
      default:
        return <ReceiptLongIcon sx={{ fontSize: iconSize, color: iconColor }} />;
    }
  };

  // دالة لعرض عناصر الشبكة
  const renderGridItems = (items) => {
    return (
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
        {items.map((item, index) => (
          <Grid 
            item 
            xs={6} 
            sm={4} 
            md={3} 
            lg={2.4} 
            key={index}
          >
            <ButtonBase
              onClick={() => navigate(item.link)}
              sx={{
                width: "100%",
                height: { xs: 120, sm: 130, md: 140 },
                borderRadius: { xs: 2, sm: 2.5, md: 3 },
                border: "2px solid #e0e0e0",
                backgroundColor: "#ffffff",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: { xs: "translateY(-3px)", sm: "translateY(-4px)", md: "translateY(-5px)" },
                  boxShadow: { xs: "0 6px 20px rgba(0,0,0,0.12)", sm: "0 8px 25px rgba(0,0,0,0.15)", md: "0 10px 30px rgba(0,0,0,0.18)" },
                  borderColor: "#1976d2",
                  backgroundColor: "#f8fafc",
                },
                "&:active": {
                  transform: "translateY(0px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                p={{ xs: 1.5, sm: 2, md: 2.5 }}
                textAlign="center"
              >
                {item.icon}
                <Typography
                  variant="body1"
                  fontWeight="600"
                  mt={{ xs: 1, sm: 1.5 }}
                  color="text.primary"
                  sx={{
                    fontSize: {
                      xs: '0.85rem',
                      sm: '0.95rem',
                      md: '1rem'
                    },
                    lineHeight: 1.3,
                    px: 1
                  }}
                >
                  {item.name}
                </Typography>
              </Box>
            </ButtonBase>
          </Grid>
        ))}
      </Grid>
    );
  };

  // جلب أنواع الفواتير الخاصة بالمشتريات وتجهيزها
  const purchaseInvoiceOptions = invoiceTypes
    .filter((type) => {
        const name = type.name.trim();
        const isPurchase = ["شراء", "فاتورة شراء"].includes(name);
        const isPurchaseReturn = ["مرتجع مشتريات", "فاتورة مرتجع مشتريات"].includes(name);

        if (isPurchase) return hasPermission(PERMISSIONS.INVOICE_PURCHASE_CREATE);
        if (isPurchaseReturn) return hasPermission(PERMISSIONS.INVOICE_PURCHASE_RETURN_CREATE);

        return false;
    })
    .map((type) => {
      let translationKey = '';
      switch (type.name.trim()) {
        case "شراء":
        case "فاتورة شراء":
          translationKey = 'purchasesMenu.purchaseInvoice';
          break;
        case "مرتجع مشتريات":
        case "فاتورة مرتجع مشتريات":
          translationKey = 'purchasesMenu.purchaseReturn';
          break;
        default:
          translationKey = 'purchasesMenu.purchaseInvoice';
      }
      
      return {
        name: t(translationKey),
        icon: getPurchaseTypeIcon(type.name),
        link: `/invoices/new/${type.invoiceTypeId}`,
      };
    });

  // تعريف أقسام القائمة
  const invoiceSection = [
    ...purchaseInvoiceOptions,
    hasPermission(PERMISSIONS.VOUCHER_CREATE) && { 
      name: t('purchasesMenu.supplierPayment'), 
      icon: <PaymentIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#f44336" }} />, 
      link: "/vouchers/supplier-payment" 
    },
    hasPermission(PERMISSIONS.SUPPLIER_CREATE) && { 
      name: t('purchasesMenu.addSupplier'), 
      icon: <LocalShippingIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#673ab7" }} />, 
      link: "/Supplier/new" 
    },
    hasPermission(PERMISSIONS.SUPPLIER_VIEW) && { 
      name: t('purchasesMenu.suppliersList'), 
      icon: <GroupIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#673ab7" }} />, 
      link: "/Supplier" 
    },
  ].filter(Boolean);

  const reportSection = [
    hasPermission(PERMISSIONS.INVOICE_PURCHASE_VIEW) && {
      name: t('purchasesMenu.allPurchaseInvoices'),
      icon: <ListAltIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#3b82f6" }} />,
      link: "/reports/all-invoices?direction=1&invoiceTypeId=2",
    },
    hasPermission(PERMISSIONS.INVOICE_PURCHASE_RETURN_VIEW) && {
      name: t('purchasesMenu.allPurchaseReturns'),
      icon: <ListAltIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#3b82f6" }} />,
      link: "/reports/all-invoices?direction=1&invoiceTypeId=4",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('purchasesMenu.aggregatedItemsPurchaseReport'),
      icon: <SummarizeIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#009688" }} />,
      link: "/reports/aggregated-purchase-items",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('purchasesMenu.detailedItemsPurchaseReport'),
      icon: <BarChartIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#009688" }} />,
      link: "/reports/detailed-purchase-items",
    },
    hasPermission(PERMISSIONS.REPORT_VIEW) && {
      name: t('purchasesMenu.dailyPurchaseReport'),
      icon: <CalendarTodayIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#009688" }} />,
      link: "/reports/daily-purchases",
    },
  ].filter(Boolean);

  if (loading) {
    return (
      <Container 
        maxWidth={false} 
        sx={{ 
          mt: { xs: 2, sm: 4, md: 6 },
          px: { xs: 1, sm: 2, md: 3 }
        }} 
        dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
      >
        <Box textAlign="center" py={4}>
          <CircularProgress size={isMobile ? 40 : 50} />
          <Typography 
            variant={isMobile ? "body1" : "h6"}
            sx={{ mt: 2 }}
          >
            {t('purchasesMenu.loading')}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container 
        maxWidth={false} 
        sx={{ 
          mt: { xs: 2, sm: 4, md: 6 },
          px: { xs: 1, sm: 2, md: 3 }
        }} 
        dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
      >
        <Box textAlign="center" py={2}>
          <Typography 
            color="error"
            variant={isMobile ? "body1" : "h6"}
          >
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        mt: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 3, sm: 4, md: 5 }
      }} 
      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
    >
      {/* Header مع أزرار التنقل */}
      <Paper 
        elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 3, sm: 4, md: 5 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 2, sm: 3, md: 3.5 },
          }}
        >
          <Typography 
            variant={isMobile ? "h5" : isTablet ? "h4" : "h3"} 
            fontWeight="bold" 
            color="white"
            sx={{
              fontSize: {
                xs: '1.5rem',
                sm: '2rem',
                md: '2.5rem'
              },
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            {t('purchasesMenu.dashboardTitle')}
          </Typography>

          
        </Box>
      </Paper>

      {/* قسم فواتير المشتريات */}
      <Paper 
        elevation={isMobile ? 3 : 4} 
        sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 }, 
          mb: { xs: 3, sm: 4, md: 5 }, 
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          border: '1px solid #ffebee',
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          fontWeight="bold" 
          mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
          color="error"
          sx={{
            fontSize: {
              xs: '1.15rem',
              sm: '1.35rem',
              md: '1.5rem'
            },
            borderBottom: '3px solid #d32f2f',
            paddingBottom: 1.5,
          }}
        >
          📦 {t('purchasesMenu.invoicesSection')}
        </Typography>
        {renderGridItems(invoiceSection)}
      </Paper>
      
      {/* قسم تقارير المشتريات */}
      <Paper 
        elevation={isMobile ? 3 : 4} 
        sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 }, 
          mb: { xs: 2, sm: 3, md: 4 }, 
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          border: '1px solid #e0f2f1',
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          fontWeight="bold" 
          mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
          sx={{
            fontSize: {
              xs: '1.15rem',
              sm: '1.35rem',
              md: '1.5rem'
            },
            borderBottom: '3px solid #009688',
            paddingBottom: 1.5,
            color: '#009688',
          }}
        >
          📊 {t('purchasesMenu.reportsSection')}
        </Typography>
        {renderGridItems(reportSection)}
      </Paper>
    </Container>
  );
}
